import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { documentTemplatesApi } from '../services/api';

const P  = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';

const CATEGORIES = [
  'AI Governance',
  'Security Policy',
  'CMMC',
  'Incident Response',
  'Risk & Compliance',
  'Employee Training',
  'Vendor Management',
  'General',
];

const TYPE_LABELS = {
  aup:               'Acceptable Use Policy',
  msa_addendum:      'MSA / Contract Addendum',
  governance_charter:'Governance Charter / RACI',
  governance_report: 'Governance Report',
  incident_response: 'Incident Response Plan',
  tool_inventory:    'Tool / Asset Inventory',
  tool_intake_sop:   'SOP / Procedure',
  hipaa_addendum:    'HIPAA Addendum',
  cmmc_addendum:     'CMMC / CUI Addendum',
  assessment_report: 'Assessment Report',
  cadence_review:    'Cadence Review / Notes',
  technical_report:  'Technical Report',
  training_record:   'Training Attestation',
  vendor_dd:         'Vendor Due Diligence',
  risk_register:     'Risk Register',
  wisp:              'WISP / Written Info Security Plan',
  security_plan:     'Security Plan',
  other:             'Other',
};

const APPLICABILITY_LABELS = {
  universal:      'Universal — all clients',
  operations_up:  'Operations + Strategic tiers',
  strategic_only: 'Strategic tier only',
  healthcare:     'Healthcare (HIPAA-scoped)',
  defense_cmmc:   'Defense contractors (CMMC)',
  financial:      'Financial / SOX-scoped',
};

const CATEGORY_COLORS = {
  'AI Governance':     { bg: '#F3EEFA', color: '#4A2B70' },
  'Security Policy':   { bg: '#DBEAFE', color: '#1E40AF' },
  'CMMC':              { bg: '#FEE2E2', color: '#991B1B' },
  'Incident Response': { bg: '#FEF3C7', color: '#92400E' },
  'Risk & Compliance': { bg: '#D1FAE5', color: '#065F46' },
  'Employee Training': { bg: '#E0E7FF', color: '#3730A3' },
  'Vendor Management': { bg: '#FCE7F3', color: '#9D174D' },
  'General':           { bg: '#F3F4F6', color: '#374151' },
};

const AUTO_FIELD_KEYS = new Set([
  'client_name', 'contact_name', 'ir_contact_name', 'contact_email',
  'ir_contact_email', 'client_address', 'client_phone', 'client_industry',
  'today', 'document_date', 'document_version',
  'last_assessment_date', 'assessment_type',
  'last_scan_date', 'critical_vulns', 'high_vulns',
  'last_audit_date', 'dw_exposure_count', 'last_dw_scan_date',
]);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const primaryBtn = {
  padding: '8px 18px', backgroundColor: P, color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const outlineBtn = {
  padding: '8px 18px', backgroundColor: 'transparent', color: P,
  border: `2px solid ${P}`, borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(74,43,112,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalBox = {
  background: 'white', borderRadius: 12, padding: 28,
  boxShadow: '0 20px 60px rgba(74,43,112,0.2)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn   = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF' };
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: PD };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6,
  fontSize: 14, color: '#374151', boxSizing: 'border-box', outline: 'none',
};
const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700,
  color: PD, textTransform: 'uppercase', letterSpacing: '0.05em',
};

function CategoryBadge({ category }) {
  const c = CATEGORY_COLORS[category] || CATEGORY_COLORS.General;
  return (
    <span style={{
      background: c.bg, color: c.color, borderRadius: 10,
      padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {category || 'General'}
    </span>
  );
}

export default function MasterDocumentTemplatesAdmin() {
  const navigate = useNavigate();
  const fileRef  = useRef();

  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterCat, setFilterCat]   = useState('');
  const [showAll, setShowAll]       = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '', category: 'General', type: 'other', version: '1.0',
    description: '', applicability: 'universal', tier_min: '',
  });
  const [uploadFile, setUploadFile] = useState(null);

  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await documentTemplatesApi.listTemplates(!showAll, filterCat);
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showAll, filterCat]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Select a .docx file'); return; }
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('name', uploadForm.name || uploadFile.name.replace(/\.docx$/i, ''));
    Object.entries(uploadForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
    setUploading(true);
    try {
      await documentTemplatesApi.uploadTemplate(fd);
      toast.success('Template uploaded');
      setShowUpload(false);
      setUploadForm({ name: '', category: 'General', type: 'other', version: '1.0', description: '', applicability: 'universal', tier_min: '' });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await documentTemplatesApi.updateTemplate(editId, editForm);
      toast.success('Template saved');
      setEditId(null);
      load();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleScanFields = async () => {
    setScanning(true);
    try {
      const res = await documentTemplatesApi.scanTemplateFields(editId);
      setEditForm(f => ({ ...f, merge_fields: res.data.fields || [] }));
      toast.success(`Found ${res.data.count} merge fields`);
    } catch {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const updateFieldDef = (idx, key, val) => {
    setEditForm(f => {
      const mf = [...(f.merge_fields || [])];
      mf[idx] = { ...mf[idx], [key]: val };
      return { ...f, merge_fields: mf };
    });
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this template? It will no longer appear in document generation.')) return;
    try {
      await documentTemplatesApi.deactivateTemplate(id);
      toast.success('Template deactivated');
      load();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const openEdit = (t) => {
    setEditId(t.id);
    let mf = t.merge_fields;
    if (typeof mf === 'string') { try { mf = JSON.parse(mf); } catch { mf = []; } }
    setEditForm({
      name: t.name, description: t.description || '',
      category: t.category || 'General', version: t.version,
      is_active: t.is_active, merge_fields: mf || [],
      applicability: t.applicability || 'universal',
      tier_min: t.tier_min || '',
    });
  };

  const filtered = filterCat
    ? templates.filter(t => t.category === filterCat)
    : templates;

  return (
    <div>
      <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
        ← Admin
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: PD, fontSize: 28, fontWeight: 700 }}>Master Document Templates</h2>
          <p style={{ margin: '4px 0 0', color: '#7E4DB3', fontSize: 14 }}>
            Upload and manage DOCX templates with <code style={{ background: PL, padding: '1px 5px', borderRadius: 3 }}>{'{{merge_fields}}'}</code> for all client documents.
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} style={primaryBtn}>+ Upload Template</button>
      </div>

      {/* Auto-field reference */}
      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
        <strong style={{ color: '#92400E', fontSize: 13 }}>Auto-populated fields (use in any .docx template):</strong>
        <div style={{ fontSize: 12, color: '#78350F', marginTop: 4, lineHeight: 2 }}>
          {[
            '{{client_name}}', '{{contact_name}}', '{{contact_email}}', '{{today}}',
            '{{document_version}}', '{{last_assessment_date}}', '{{last_scan_date}}',
            '{{critical_vulns}}', '{{high_vulns}}', '{{last_audit_date}}',
            '{{dw_exposure_count}}', '{{client_address}}', '{{client_phone}}',
          ].map(f => (
            <code key={f} style={{ background: '#FEF3C7', padding: '1px 5px', borderRadius: 3, marginRight: 6 }}>{f}</code>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
          {['', ...CATEGORIES].map(cat => (
            <button
              key={cat || '__all'}
              onClick={() => setFilterCat(cat)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: 'none',
                background: filterCat === cat ? P : PL,
                color: filterCat === cat ? 'white' : PD,
              }}
            >
              {cat || 'All Categories'}
            </button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280', cursor: 'pointer', flexShrink: 0 }}>
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          Show inactive
        </label>
      </div>

      {/* Templates table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7E4DB3' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', color: '#9CA3AF' }}>
          No templates found.{' '}
          <button onClick={() => setShowUpload(true)} style={{ color: P, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Upload the first one.
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PL }}>
                {['Name', 'Category', 'Type', 'Version', 'Status', 'Uploaded', ''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA', opacity: t.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: PD }}>
                    {t.name}
                    {t.description && <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 400, marginTop: 2 }}>{t.description}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}><CategoryBadge category={t.category} /></td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{TYPE_LABELS[t.type] || t.type}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>v{t.version}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: t.is_active ? '#D1FAE5' : '#E5E7EB', color: t.is_active ? '#065F46' : '#6B7280', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>
                    {fmtDate(t.uploaded_at)}
                    {t.uploaded_by_name && <div style={{ fontSize: 11, color: '#9CA3AF' }}>by {t.uploaded_by_name}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(t)} style={{ ...outlineBtn, padding: '4px 10px', fontSize: 12 }}>
                        Edit / Fields
                      </button>
                      {t.is_active && (
                        <button onClick={() => handleDeactivate(t.id)} style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #FCA5A5', color: '#DC2626', background: 'white', borderRadius: 6, cursor: 'pointer' }}>
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 520 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Upload Template</h3>
              <button onClick={() => setShowUpload(false)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleUpload}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>File (.docx) *</label>
                  <input ref={fileRef} type="file" accept=".docx"
                    onChange={e => {
                      const f = e.target.files[0];
                      setUploadFile(f);
                      if (f && !uploadForm.name) setUploadForm(p => ({ ...p, name: f.name.replace(/\.docx$/i, '') }));
                    }}
                    style={{ display: 'none' }}
                  />
                  <div onClick={() => fileRef.current.click()}
                    style={{ border: '2px dashed #E5D5F5', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', background: uploadFile ? '#F0FDF4' : 'white' }}
                  >
                    {uploadFile
                      ? <span style={{ color: '#065F46', fontWeight: 600 }}>✓ {uploadFile.name}</span>
                      : <span style={{ color: '#9CA3AF' }}>Click to select a .docx file</span>
                    }
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Template Name *</label>
                  <input type="text" value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="e.g. WISP v2.1" required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select value={uploadForm.category} onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))} style={inputStyle}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <select value={uploadForm.type} onChange={e => setUploadForm(p => ({ ...p, type: e.target.value }))} style={inputStyle}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Version</label>
                    <input type="text" value={uploadForm.version} onChange={e => setUploadForm(p => ({ ...p, version: e.target.value }))} style={inputStyle} placeholder="1.0" />
                  </div>
                  <div>
                    <label style={labelStyle}>Applicability</label>
                    <select value={uploadForm.applicability} onChange={e => setUploadForm(p => ({ ...p, applicability: e.target.value }))} style={inputStyle}>
                      {Object.entries(APPLICABILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Optional…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setShowUpload(false)} style={outlineBtn} disabled={uploading}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit / Fields modal */}
      {editId && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Edit Template</h3>
              <button onClick={() => setEditId(null)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select value={editForm.category || 'General'} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Version</label>
                    <input type="text" value={editForm.version} onChange={e => setEditForm(f => ({ ...f, version: e.target.value }))} style={inputStyle} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', cursor: 'pointer', paddingTop: 24 }}>
                    <input type="checkbox" checked={!!editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                    Active
                  </label>
                </div>
                <div>
                  <label style={labelStyle}>Applicability</label>
                  <select value={editForm.applicability || 'universal'} onChange={e => setEditForm(f => ({ ...f, applicability: e.target.value }))} style={inputStyle}>
                    {Object.entries(APPLICABILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>

                {/* Merge fields */}
                <div style={{ borderTop: '1px solid #E5D5F5', paddingTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Merge Fields</label>
                    <button type="button" onClick={handleScanFields} disabled={scanning}
                      style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px' }}>
                      {scanning ? 'Scanning…' : '⟳ Scan Template'}
                    </button>
                  </div>
                  {(!editForm.merge_fields || editForm.merge_fields.length === 0) ? (
                    <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                      Click "Scan Template" to read {'{{placeholders}}'} from the .docx file.
                    </div>
                  ) : (
                    <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #E5D5F5', borderRadius: 6 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: PL, position: 'sticky', top: 0 }}>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Placeholder</th>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Label</th>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editForm.merge_fields.map((f, idx) => (
                            <tr key={idx} style={{ borderTop: '1px solid #F3EEFA', background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: PD, whiteSpace: 'nowrap' }}>
                                {'{{'}{f.key}{'}}'}
                              </td>
                              <td style={{ padding: '4px 10px' }}>
                                <input type="text" value={f.label || ''} onChange={e => updateFieldDef(idx, 'label', e.target.value)}
                                  style={{ width: '100%', border: '1px solid #E5D5F5', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                              </td>
                              <td style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600 }}>
                                {(f.auto || AUTO_FIELD_KEYS.has((f.key || '').toLowerCase()))
                                  ? <span style={{ color: '#065F46' }}>Auto</span>
                                  : <span style={{ color: '#9CA3AF' }}>Manual</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setEditId(null)} style={outlineBtn} disabled={saving}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
